// import firebase from "firebase";
import {Suspense, lazy, useEffect} from 'react';
import {CodeProvider} from 'contexts/CodeContext';
import {SplashPageRoute} from 'landing/home/SplashPageRoute';
import {TeamPageRoute} from 'landing/team/TeamPageRoute';
import {readBuildCommit} from 'buildCommitMeta';
import {watchForStaleBuild} from 'staleBuildUpdate';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useMatch,
  useParams,
} from 'react-router-dom';
import {QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {CourseIndexRoute} from 'course/splash/CourseHomeRoute';

// set up the style
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/style.scss';

import {Authenticated} from './components/auth/Authenticated';
import ErrorBoundary from 'components/errors/ErrorBoundary';
import {EmailActionHandler} from 'course/emailActionHandler/EmailActionHandler';
import {CourseProvider} from 'contexts/CourseContext';
import {ProfileProvider} from 'contexts/ProfileContext';

import {WelcomeChoice} from 'landing/welcome';
import {Unsubscribe} from 'components/auth/Unsubscribe';
import {InterestForm} from 'forms/Interest';
import {CustomAuth} from 'components/auth/CustomAuth';
import {ApplyRouteGate} from 'course/application/general/components/ApplyRouteGate';
import {ApplicationJoinLayout} from 'course/application/layout/ApplicationJoinLayout';
import {ApplicationTaskProgressProvider} from 'course/application/layout/ApplicationTaskProgressProvider';
import {CommonAboutForm} from 'course/application/form/CommonAboutForm';
import {Debugging} from 'course/application/sectionLeaderApplication/debugging/Debugging';
import {getParentCourseId} from 'course/application/general/utils/apputils';
import {ErrorPage} from 'components/errors/ErrorPage';
import {TranslationProvider} from 'contexts/translation/TranslationContext';
import CipAnalyticsWrapperRoute from 'engagement/CipAnalyticsWrapperRoute';
import {LoadingTest} from 'components/loading/LoadingTest';
import {Loading} from 'components/loading/Loading';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
import {StarredProjectsForGlobalPage} from 'course/starredProjects/StarredProjectsPage';
import {DeleteAccount} from 'components/auth/DeleteAccount';
import GlobalDevelopmentOnlyRoutes from 'routes/GlobalDevelopmentOnlyRoutes';
import {buildLazyRoute} from 'routes/LazyRoutes';
import {queryClient} from 'config';

// Lazy imports load only when the page is visited, reducing initial load time

// The whole `/:courseId/*` subtree. Loaded lazily because it is by far the
// heaviest part of the app — it transitively pulls in the IDE, Monaco,
// lessons, puzzles, and every other course subsystem. Keeping it out of the
// entry chunk is what makes the landing/apply pages load fast, most visibly
// PAI's splash (see [#3616](https://github.com/CodeinPlace/cip/issues/3616)).
// Everything that must be configured before a course page mounts an editor is
// imported by CourseRoutes itself (see the monaco/configureLoader import
// there).
const CourseRoutes = lazy(() => import('./CourseRoutes'));
// StudentApplication's header renders the TipTap rich-text editor, whose
// runnable-code extensions transitively import monaco-editor — lazy for the
// same reason as CourseRoutes above.
const StudentApplication = lazy(() =>
  import(
    'course/application/studentApplication/splash/StudentApplication'
  ).then(module => ({default: module.StudentApplication})),
);
// Explore transitively imports monaco-editor via the application code editor —
// lazy for the same reason as CourseRoutes above.
const Explore = lazy(() =>
  import('course/application/explore/Explore').then(module => ({
    default: module.Explore,
  })),
);
// These three render the TipTap rich-text editor (whose runnable-code
// extensions transitively import monaco-editor) — lazy for the same reason
// as CourseRoutes above.
const SectionLeaderApplication = lazy(() =>
  import(
    'course/application/sectionLeaderApplication/splash/SectionLeaderApplication'
  ).then(module => ({default: module.SectionLeaderApplication})),
);
const OpenSectionsApplicationPage = lazy(
  () =>
    import('course/application/openSections/OpenSectionsApplicationPage'),
);
const TeachingDemo = lazy(() =>
  import(
    'course/application/sectionLeaderApplication/teachingDemo/TeachingDemo'
  ).then(module => ({default: module.TeachingDemo})),
);
// The lesson viewer transitively imports the duolessons slide renderers
// (including the CodeAdventure slide type, which pulls in monaco-editor) —
// lazy for the same reason as CourseRoutes above.
const ApplicationLessonPreview = lazy(() =>
  import(
    'course/application/layout/ApplicationLessonPreview'
  ).then(module => ({default: module.ApplicationLessonPreview})),
);
// The math IDE transitively imports monaco-editor and Pyodide — lazy for the
// same reason as CourseRoutes above.
const ApplicationMathIdePreview = lazy(() =>
  import(
    'course/application/layout/ApplicationMathIdePreview'
  ).then(module => ({default: module.ApplicationMathIdePreview})),
);
const BlogPage = lazy(() =>
  import('blog/BlogPage').then(module => ({default: module.BlogPage})),
);
const ContactUs = lazy(() => import('./landing/contactUs/default'));
const AlumRoutes = lazy(() => import('./alumni/AlumniRoutes'));
const AlumniHome = lazy(() => import('./alumni/pages/Home'));
const AlumPageContainer = lazy(() => import('./alumni/AlumniPage'));
const DeleteWhatsappData = lazy(
  () => import('./components/auth/DeleteWhatsappData'),
);
const ProjectShowcaseComponent = lazy(
  () => import('course/finalproject/showcase/ProjectShowcase'),
);
const SimStudentLanding = lazy(
  () => import('./course/simteach/SimStudentLanding'),
);
const PaiSectionTeachingRoute = lazy(
  () => import('./course/paisection/PaiSectionTeachingRoute'),
);
const GlobalAdmin = lazy(() =>
  import('./course/editors/adminEditor/GlobalAdmin').then(module => ({
    default: module.GlobalAdmin,
  })),
);
const ExperimentsDashboard = lazy(() =>
  import('./course/editors/adminEditor/experiments/ExperimentsDashboard').then(
    module => ({default: module.ExperimentsDashboard}),
  ),
);
// TODO: @piech. Refactor: pull Authenticated for the course page to the outer router

/*
 * This contains the broader route initialization.
 * If a route does not belong to a course, put it here.
 */
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path="/"
      element={<CipAnalyticsWrapperRoute />}
      errorElement={<ErrorBoundary />}
    >
      {/* Landing website — SplashPageRoute checks AppOverridesContext so
          external wrappers (e.g. PAI) can substitute their own landing page
          without touching CIP source files. */}
      <Route index element={<SplashPageRoute />} />
      {buildLazyRoute({
        path: 'terms-of-service',
        loader: () => import('./landing/termsOfService/default'),
      })}
      {/* Public, no-login LaTeX-to-image tool. See tools/equationEditor/README.md. */}
      {buildLazyRoute({
        path: 'equationeditor',
        loader: () => import('./tools/equationEditor/default'),
      })}
      {/* TeamPageRoute checks AppOverridesContext so external wrappers (e.g.
          PAI) can substitute their own team page without touching CIP source. */}
      <Route path="team" element={<TeamPageRoute />} />

      {/* Code in Place X info page. Static path wins over the `:courseId`
          catch-all below, so `/cipx/intro` is not treated as a course route. */}
      {buildLazyRoute({
        path: 'cipx/intro',
        loader: () => import('./landing/cipx/default'),
      })}
      {buildLazyRoute({
        path: 'cipx/ai-policy',
        loader: () => import('./landing/aiPolicy/default'),
      })}
      {/* The course description used to live here, before it took over
          `cipx/intro`. Kept as a redirect so links already shared keep working. */}
      <Route
        path="cipx/about"
        element={<Navigate to="/cipx/intro" replace />}
      />

      {/* Giving page. Course-agnostic and unauthenticated so one link works
          from any surface, for signed-in students and strangers alike. */}
      {buildLazyRoute({
        path: 'give',
        loader: () => import('./landing/give/default'),
      })}
      {buildLazyRoute({
        path: 'showcase',
        loader: () =>
          import('course/starredProjects/StarredProjectsPage').then(module => ({
            default: () => (
              <QueryClientProvider client={queryClient}>
                <module.StarredProjectsForGlobalPage />
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            ),
          })),
      })}
      {buildLazyRoute({
        path: '2023/showcase',
        loader: () => import('course/finalproject/showcase/ProjectShowcase'),
        componentProps: {
          courseId: 'cip3',
        },
      })}
      {buildLazyRoute({
        path: '2024/showcase',
        loader: () => import('course/finalproject/showcase/ProjectShowcase'),
        componentProps: {
          courseId: 'cip4',
        },
      })}
      {buildLazyRoute({
        path: '2025/showcase',
        loader: () => import('course/finalproject/showcase/ProjectShowcase'),
        componentProps: {
          courseId: 'cip5',
        },
      })}
      <Route
        path="sim"
        element={
          <CodeProvider>
            <Suspense fallback={<Loading />}>
              <SimStudentLanding />
            </Suspense>
          </CodeProvider>
        }
      />
      <Route path="emailactionhandler" element={<EmailActionHandler />} />
      <Route
        path="welcome"
        element={<Authenticated component={WelcomeChoice} />}
      ></Route>

      <Route
        path="unsubscribe"
        element={<Authenticated component={Unsubscribe} />}
      ></Route>
      <Route
        path="delete-my-account"
        element={<Authenticated component={DeleteAccount} />}
      ></Route>

      <Route path="loadingtest" element={<LoadingTest />} />

      <Route
        path="delete-whatsapp-data"
        element={
          <Suspense>
            <DeleteWhatsappData />
          </Suspense>
        }
      />

      <Route path="interest" element={<InterestForm />}></Route>
      <Route path="oauth2" element={<CustomAuth />}></Route>
<Route
        path="admin"
        element={
          <ProfileProvider>
            <Suspense fallback={<Loading />}>
              <Authenticated component={GlobalAdmin} />
            </Suspense>
          </ProfileProvider>
        }
      />
      <Route
        path="admin/experiments"
        element={
          <ProfileProvider>
            <Suspense fallback={<Loading />}>
              <Authenticated component={ExperimentsDashboard} />
            </Suspense>
          </ProfileProvider>
        }
      />
      <Route
        path="blog/:blogId"
        element={
          <Suspense fallback={<Loading />}>
            <Authenticated component={BlogPage} />
          </Suspense>
        }
      />

      <Route path="2021/showcase" element={<ShowcaseRedirect />} />
      <Route path="2021/showcase/:showcaseId" element={<ShowcaseRedirect />} />
      <Route
        path="contact"
        element={
          <Suspense>
            <ContactUs />
          </Suspense>
        }
      />

      <Route
        path="alumni"
        element={<Authenticated component={AlumPageContainer} />}
      >
        <Route path="*" element={<AlumRoutes queryClient={queryClient} />} />
        <Route index element={<AlumniHome />} />
      </Route>
      {/* Development-only routes that only show up in the development environment */}
      <Route path="dev/*" element={<GlobalDevelopmentOnlyRoutes />} />

      <Route
        path=":courseId/showcase"
        element={
          <CourseProvider>
            <TranslationProvider>
              <Suspense fallback={<Loading />}>
                <ProjectShowcaseComponent />
              </Suspense>
            </TranslationProvider>
          </CourseProvider>
        }
      />

      {/* Course-scoped routes. Two URL shapes address a course — the general
          tree (`/:courseId/…`) and the application short link
          (`/apply/:targetCourseId/…`). Both nest under this ONE pathless
          layout so the course-wide providers stay mounted when navigating
          between them; CourseScopeLayout (below) tells the full story. */}
      <Route element={<CourseScopeLayout />}>
        {/* Application short link, e.g. `/apply/pai1/student`. The static
            `apply` segment outranks the `:courseId` sibling below (same
            ranking rule that keeps `cipx/intro` out of the course tree).
            ApplyRouteGate decides which courses live here: PAI-parented
            courses always do, any other course opts in via the course
            document's `applyShortLinkEnabled` flag (an Admin-editor
            checkbox), and everything else redirects to its long-form
            canonical splash route.
            The sub-routes mirror `join`/`applyteach` in CourseRoutes.tsx
            exactly — without them, going from the splash page to "About You"
            or "About the job" would cross route subtrees and remount the
            whole application flow (re-auth + refetch) instead of swapping
            the layout's <Outlet/> in place. */}
        <Route path="apply/:targetCourseId" element={<ApplyRouteGate />}>
          {/* Pathless layout mounted once for every child route below, so a
              lesson/assignment preview shares its `lessonsProgress`/
              `assnProgress` subscriptions with the splash/"About You" pages
              instead of resubscribing on every hop between them. See
              ApplicationTaskProgressProvider's own doc comment for why this
              can't just be the full CompletionProvider. */}
          <Route element={<ApplicationTaskProgressProvider />}>
            <Route path="student" element={<ApplicationJoinLayout />}>
              <Route
                index
                element={
                  <Suspense fallback={<Loading />}>
                    <StudentApplication />
                  </Suspense>
                }
              />
              <Route path="about" element={<CommonAboutForm />} />
              <Route
                path="aboutCourse"
                element={
                  <Suspense fallback={<Loading />}>
                    <Explore applicationType="student" />
                  </Suspense>
                }
              />
              {/* A cheap lesson preview for a "lesson" checklist item — see
                  ApplicationLessonPreview's doc comment. Nested INSIDE the
                  student flow (mirrors `mathteachable` under `sl` below) so
                  entering it from the checklist swaps only
                  ApplicationJoinLayout's <Outlet/> — the shared application
                  data stays warm and there is no full reload on exit. */}
              <Route
                path="learn/:lessonId/:slideId?"
                element={
                  <Suspense fallback={<Loading />}>
                    <ApplicationLessonPreview />
                  </Suspense>
                }
              />
              {/* A math-IDE assignment preview for a "code" checklist item —
                  see ApplicationMathIdePreview's doc comment. Same nesting
                  rationale as `learn` above. */}
              <Route
                path="mide/a/:assignmentId"
                element={
                  <Suspense fallback={<Loading />}>
                    <ApplicationMathIdePreview />
                  </Suspense>
                }
              />
            </Route>
            <Route path="sl" element={<ApplicationJoinLayout />}>
              <Route
                index
                element={
                  <Suspense fallback={<Loading />}>
                    <SectionLeaderApplication />
                  </Suspense>
                }
              />
              <Route path="about" element={<CommonAboutForm />} />
              <Route
                path="aboutJob"
                element={
                  <Suspense fallback={<Loading />}>
                    <Explore applicationType="sl" />
                  </Suspense>
                }
              />
              <Route
                path="teachingDemo"
                element={
                  <Suspense fallback={<Loading />}>
                    <TeachingDemo />
                  </Suspense>
                }
              />
              <Route path="debugging" element={<Debugging />} />
              <Route
                path="open-sections"
                element={
                  <Suspense fallback={<Loading />}>
                    <OpenSectionsApplicationPage />
                  </Suspense>
                }
              />
              <Route
                path="mathteachable"
                element={
                  <Suspense fallback={<Loading />}>
                    <PaiSectionTeachingRoute />
                  </Suspense>
                }
              />
              {/* See the matching comments under `student` above. */}
              <Route
                path="learn/:lessonId/:slideId?"
                element={
                  <Suspense fallback={<Loading />}>
                    <ApplicationLessonPreview />
                  </Suspense>
                }
              />
              <Route
                path="mide/a/:assignmentId"
                element={
                  <Suspense fallback={<Loading />}>
                    <ApplicationMathIdePreview />
                  </Suspense>
                }
              />
            </Route>
          </Route>
          <Route
            path="*"
            element={<ErrorPage msg="Unknown application link" />}
          />
        </Route>

        {/* The general course tree: the first URL segment names the course. */}
        <Route path=":courseId">
          <Route
            path="*"
            element={
              <Suspense fallback={<Loading />}>
                <CourseRoutes queryClient={queryClient} />
              </Suspense>
            }
          />
          <Route index element={<CourseIndexRoute />} />
        </Route>
      </Route>
    </Route>,
  ),
);

/**
 * Pathless layout that establishes which course a URL is scoped to, and
 * mounts the course-wide providers (course document, code runner,
 * translations) exactly once for BOTH URL shapes that address a course:
 *
 *   `/:courseId/…`             — the course names itself, e.g. `/pai/ide/a/warmup`
 *   `/apply/:targetCourseId/…` — the application short link, scoped to the
 *                                target course's parent, e.g. `pai` for `pai1`
 *
 * React Router keeps a route's element mounted for as long as that route
 * stays in the matched branch — identity is per route object, not per
 * rendered value. Because this single layout route is the parent of both
 * subtrees, navigating between them (say, PAI's application splash → a
 * lesson under `/pai/…`) never unmounts the providers, and their data
 * fetches — keyed on the resolved course id, which is identical under both
 * shapes — never re-run. If each subtree mounted its own provider stack
 * instead, every such hop would refetch the course document and translations
 * and restart the code runtime.
 */
function CourseScopeLayout() {
  // An ancestor layout cannot read its children's route params (React Router
  // resolves params top-down), so match the two URL shapes against the
  // location directly. Order matters: `/apply/pai1/…` also matches the
  // generic `/:courseId/*` pattern (as courseId="apply"), so test the short
  // link first. The bare `/:courseId` pattern covers course home pages.
  const applyMatch = useMatch('/apply/:targetCourseId/*');
  const courseMatch = useMatch('/:courseId/*');
  const courseHomeMatch = useMatch('/:courseId');

  // The same application pages are served at both the short link and their
  // long-form paths under the course tree, so both have to be recognized.
  const joinMatch = useMatch('/:courseId/join/:targetCourseId/*');
  const applyTeachMatch = useMatch('/:courseId/applyteach/:targetCourseId/*');
  const isApplicationRoute = Boolean(applyMatch ?? joinMatch ?? applyTeachMatch);

  const courseId = applyMatch?.params.targetCourseId
    ? getParentCourseId(applyMatch.params.targetCourseId)
    : (courseMatch ?? courseHomeMatch)?.params.courseId;

  return (
    <CourseProvider courseId={courseId}>
      {/* One profile subscription for the whole course tree. The course home
       * page and the course chrome both need it, and a provider nested below
       * for the same course defers to this one, so a home-page redirect into
       * the chrome no longer reads the user documents twice. See #4819. */}
      <ProfileProvider>
        {/* Translations are offered globally (see `src/index.js`), but when a
         * course is present, we offer course-specific translations. */}
        {/* Application pages don't run Python, and only inherit this provider
         * because they share this layout with the course tree. Course routes
         * stay eager so pre-warming hides the download before a student opens
         * the IDE or a lesson. See #4114. */}
        <CodeProvider deferUntilRequested={isApplicationRoute}>
          <TranslationProvider>
            <Outlet />
          </TranslationProvider>
        </CodeProvider>
      </ProfileProvider>
    </CourseProvider>
  );
}

export const Router = () => {
  useEffect(
    () => watchForStaleBuild({router, runningBuild: readBuildCommit()}),
    [],
  );
  return <RouterProvider router={router} />;
};

function ShowcaseRedirect() {
  const showcaseId = useParams().showcaseId || '';
  const path = `https://codeinplace-2021.netlify.app/2021/showcase/${showcaseId}`;
  useEffect(() => {
    window.location.href = path;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, []);

  return <></>;
}
